import { useCallback, useEffect, useRef, useState } from 'react'

const STORAGE_KEY = 'app_build_hash'
const SNOOZE_KEY = 'app_version_snoozed_at'
const SNOOZE_DURATION = 5 * 60 * 1000 // 5 minutes

export const useVersionCheck = (interval = 30000) => {
  const [shouldReload, setShouldReload] = useState(false)
  const initialHash = useRef<string | null>(null)

  const isSnoozed = () => {
    const snoozedAt = localStorage.getItem(SNOOZE_KEY)
    if (!snoozedAt) return false
    return Date.now() - Number(snoozedAt) < SNOOZE_DURATION
  }

  const checkVersion = useCallback(async () => {
    try {
      if (isSnoozed()) return

      const res = await fetch('/version.json?_t=' + Date.now(), {
        cache: 'no-store',
      })
      if (!res.ok) return

      const data = await res.json()
      const serverHash = data.hash as string

      if (!initialHash.current) {
        initialHash.current = serverHash
        localStorage.setItem(STORAGE_KEY, serverHash)
        return
      }

      if (serverHash !== initialHash.current) {
        localStorage.setItem(STORAGE_KEY, serverHash)
        setShouldReload(true)
      }
    } catch {
      // Network error, skip
    }
  }, [])

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      initialHash.current = stored
    }

    checkVersion()
    const id = setInterval(checkVersion, interval)
    return () => clearInterval(id)
  }, [interval, checkVersion])

  const handleReload = () => {
    localStorage.removeItem(SNOOZE_KEY)
    window.location.reload()
  }

  const handleSnooze = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now()))
    setShouldReload(false)
  }

  return { shouldReload, setShouldReload, handleReload, handleSnooze }
}
